import { propertyUtils } from '@zg-rentals/rental-manager-env';
import type { CurrentPropertyProps } from '../model/CurrentProperty';
import type { UserProps } from '../model/User';

// find a better naming
export type PageLocation = 'bottom_of_page' | 'top_hero';

export const TENANT_PLACEMENT_PARTNERS = {
  DOORSTEAD: 'doorstead',
  NOMAD: 'nomad',
  NONE: 'none',
};

export const DOORSTEAD_BASE_URL = 'https://hello.doorstead.com/adclk';
export const NOMAD_BASE_URL = 'https://nomadlease.com/tenant-placement';
export const PID_TYPES = {
  LISTING_ALIAS: 'listingAlias',
  LOT_ID: 'lotId',
};
export const UID_TYPES = {
  ZUID: 'zuid',
  ZGUID: 'zguid',
};

export const doorsteadStates: Array<string> = ['CA', 'FL', 'TX', 'WA'];
export const nomadStates: Array<string> = ['CO', 'AZ', 'GA', 'NC'];

interface UtmParams {
  utm_source: string;
  utm_campaign: string;
  utm_medium: string;
  utm_term?: string;
  utm_content?: string;
}

const getPartnerBaseUrl = (partner: string) => {
  switch (partner) {
    case TENANT_PLACEMENT_PARTNERS.NOMAD:
      return NOMAD_BASE_URL;
    default:
      return DOORSTEAD_BASE_URL;
  }
};

const getParkingParamValue = (propertyDetailsData: {
  offStreetParking?: boolean;
  garageParking?: boolean;
  hasAttachedGarageParking?: boolean;
  hasDetachedGarageParking?: boolean;
}) =>
  propertyDetailsData?.offStreetParking ||
  propertyDetailsData?.garageParking ||
  propertyDetailsData?.hasAttachedGarageParking ||
  propertyDetailsData?.hasDetachedGarageParking
    ? '1'
    : '0';

const getUtmParams = (from: string | null, utmCampaign: string, utmTerm: string | null) => {
  const baseUtmParams: UtmParams = {
    utm_source: 'zillow',
    utm_campaign: utmCampaign,
    utm_medium: 'website',
  };

  if (utmTerm) {
    baseUtmParams.utm_term = utmTerm;
  }

  if (from) {
    baseUtmParams.utm_content = from;
  }

  return baseUtmParams;
};

const getUserParams = (user: UserProps) => {
  return {
    p_uid: String(user?.encodedZuid ? user?.encodedZuid : user?.encodedGuidZuid),
    p_uid_type: user?.encodedZuid ? UID_TYPES.ZUID : UID_TYPES.ZGUID,
  };
};

export const isStateSupported = (stateCode: string) => {
  return doorsteadStates.includes(stateCode.toUpperCase()) || nomadStates.includes(stateCode.toUpperCase());
};

export const determinePartner = (stateCode: string) => {
  if (doorsteadStates.includes(stateCode.toUpperCase())) {
    return TENANT_PLACEMENT_PARTNERS.DOORSTEAD;
  }
  if (nomadStates.includes(stateCode.toUpperCase())) {
    return TENANT_PLACEMENT_PARTNERS.NOMAD;
  }
  return TENANT_PLACEMENT_PARTNERS.NONE;
};

export const getPartnerTenantPlacementUrlByPropertyDetailsData = (
  partner: string,
  propertyDetailsData: CurrentPropertyProps,
  user: UserProps,
  from: string | null,
  utmCampaign: string,
  utmTerm: string | null,
) => {
  const { formattedCompleteAddress } = propertyUtils.formatAddress(propertyDetailsData || {});
  const floorplan = propertyDetailsData?.floorplans[0];
  const bed = floorplan?.beds ?? '';
  const bath = floorplan?.baths ?? '';
  const sqft = floorplan?.squareFeet ?? '';
  const type = propertyDetailsData?.propertyType ?? '';
  const parking = getParkingParamValue(propertyDetailsData);

  const queryParams = new URLSearchParams({
    ...getUtmParams(from, utmCampaign, utmTerm),
    ...getUserParams(user),
    p_pid: propertyDetailsData?.listingAlias,
    p_pid_type: PID_TYPES.LISTING_ALIAS,
    address: formattedCompleteAddress ?? '',
    bed: String(bed),
    bath: String(bath),
    sqft: String(sqft),
    type: String(type),
    parking: String(parking),
  });

  return `${getPartnerBaseUrl(partner)}?${queryParams.toString()}`;
};

export const getPartnerTenantPlacementUrlByListingAddressAndLotId = (
  partner: string,
  listingAddress: string,
  lotId: string,
  user: UserProps,
  from: string | null,
  utmCampaign: string,
  utmTerm: string | null,
) => {
  const queryParams = new URLSearchParams({
    ...getUtmParams(from, utmCampaign, utmTerm),
    ...getUserParams(user),
    p_pid: lotId,
    p_pid_type: PID_TYPES.LOT_ID,
    address: listingAddress,
  });

  return `${getPartnerBaseUrl(partner)}?${queryParams.toString()}`;
};

export const getPartnerTenantPlacementUrl = (
  partner: string,
  user: UserProps,
  from: string | null,
  utmCampaign: string,
  utmTerm: string | null,
) => {
  const queryParams = new URLSearchParams({
    ...getUtmParams(from, utmCampaign, utmTerm),
    ...getUserParams(user),
  });

  return `${getPartnerBaseUrl(partner)}?${queryParams.toString()}`;
};
